/**
 * The ONE renderer for audit findings (P5).
 *
 * Before this, `makeFinding` output was drawn four different ways — the sidebar
 * tab (clustered, with Fix buttons), the export gate modal (flat titles, no
 * actions), the Confidence Report modal (severity groups, Review buttons) and
 * the in-ZIP HTML. Three of those had different capabilities for the same
 * object, so what the operator learned in one surface did not transfer to the
 * next. This module is what the Publish checks panel, the report view and the publish
 * sheet all render through.
 *
 * The behaviour that matters most is not visual. `PrePublishAuditTab._render()`
 * rewrote `innerHTML` wholesale on every report event, and the cheap-tier audit
 * emits one every 300ms while you type — so an open panel was torn down and
 * rebuilt five times a second, destroying focus, scroll position and any
 * half-typed input. `update()` here diffs by finding id and patches in place,
 * and REFUSES to touch the DOM structure while the list contains
 * `document.activeElement`.
 *
 * Built from ui/components.js primitives only; the consistency sweep fails the
 * build on a hand-rolled button.
 */

import { createButton } from './components.js';
import { icon } from './icons.js';
import { escapeHTML } from '../shared/html-escape.js';

const SEVERITY_LABEL = {
  blocking: 'Will break',
  warning: 'Worth fixing',
  cleanup: 'Polish'
};

/** Severity order, worst first. */
export const SEVERITY_ORDER = ['blocking', 'warning', 'cleanup'];

/** The same action wording is used by row controls and their undo feedback. */
export function acceptVerb(finding) {
  return finding?.kind === 'maintenance' ? 'Ignore' : 'Keep as is';
}

export function acceptUndoMessage(finding) {
  return finding?.kind === 'maintenance' ? 'Ignored' : 'Kept as is';
}

function savedClusterExpansion(key) {
  try {
    const value = window.sessionStorage.getItem(`pb:finding-cluster:${key}`);
    return value === null ? null : value === 'expanded';
  } catch {
    // Storage may be disabled in an embedding/private session; disclosure still works.
    return null;
  }
}

function saveClusterExpansion(key, expanded) {
  try {
    window.sessionStorage.setItem(`pb:finding-cluster:${key}`, expanded ? 'expanded' : 'collapsed');
  } catch {
    // Session preference persistence is optional when storage is unavailable/full.
  }
}

function pagesAffected(findings) {
  const pageIds = new Set();
  for (const finding of findings) {
    const location = finding.location || {};
    if (location.pageId) pageIds.add(location.pageId);
    for (const id of location.brokenOnPageIds || []) pageIds.add(id);
  }
  return pageIds.size;
}

/**
 * The line under a row's title. It must carry what VARIES between rows: six
 * rows titled "External reference" with nothing beneath them were six
 * identical lines. Export-time findings have no page, so the host, the missing
 * file or the shipped path is what tells them apart.
 */
function locationLabel(finding) {
  const loc = finding.location || {};
  const parts = [];
  if (loc.sharedKind) parts.push(`Shared ${loc.sharedKind}`);
  else if (loc.pageName) parts.push(loc.pageName);
  else {
    const where = loc.host || loc.url || loc.path || loc.target || loc.domain || '';
    if (typeof where === 'string' && where) parts.push(where);
  }
  if (Array.isArray(loc.brokenOnPageIds) && loc.brokenOnPageIds.length > 1) {
    parts.push(`${loc.brokenOnPageIds.length} pages affected`);
  }
  // Gate v4: the last download shipped this although it could not be recovered.
  if (finding.disposition === 'exportedAnyway') parts.push('Exported anyway');
  return parts.join(' · ');
}

/** Worst severity among a cluster's rows — the colour its bar takes. */
function clusterSeverity(rows) {
  let rank = SEVERITY_ORDER.length;
  for (const row of rows) {
    const index = SEVERITY_ORDER.indexOf(row?.severity);
    if (index >= 0 && index < rank) rank = index;
  }
  return SEVERITY_ORDER[rank] || 'cleanup';
}

/**
 * Group findings by what the REMEDY is, not by severity. Severity is a filter;
 * the shared fix is the grouping. A wall of forty identical rows was never
 * information — it was one piece of information forty times.
 */
export function clusterFindings(findings) {
  const clusters = [];
  const byKey = new Map();
  for (const finding of findings) {
    const key = finding.groupKey || null;
    if (!key) {
      clusters.push({ key: null, label: null, rows: [finding] });
      continue;
    }
    let cluster = byKey.get(key);
    if (!cluster) {
      // A group key is an identifier, never a label: `broken-local-ref` once
      // rendered verbatim as a cluster heading. The first row's title names
      // the remedy when the producer supplied no groupLabel.
      cluster = { key, label: finding.groupLabel || finding.title || key, rows: [] };
      byKey.set(key, cluster);
      clusters.push(cluster);
    }
    cluster.rows.push(finding);
  }
  // A cluster of one is not worth its chrome.
  return clusters.map((c) => (c.rows.length === 1 ? { ...c, key: null, label: null } : c));
}

/**
 * @param {object} opts
 * @param {object[]} opts.findings   disposed findings (carrying `disposition`)
 * @param {(f:object)=>void} [opts.onAction]  primary action for one row
 * @param {(f:object)=>void} [opts.onAccept]  accept one row
 * @param {(f:object|null)=>void} [opts.onHover] hover target, null on leave
 * @param {(f:object)=>void} [opts.onNavigate] row activation; consumers scroll
 * @param {(kind:string, rows:object[])=>void} [opts.onClusterAction]
 * @param {(filter:string)=>void} [opts.onFilterChange] user filter selection, including Escape
 * @param {boolean} [opts.collapseClusters=true] collapse large remedy groups
 * @param {boolean} [opts.showMaintenance=false] show non-blocking maintenance findings
 * @param {string} [opts.filter] severity filter, or 'accepted', or '' for all
 * @param {(f:object)=>boolean} [opts.isAcceptable] gate for the Accept affordance
 */
export function createFindingList(opts = {}) {
  const {
    onAction = null,
    onAccept = null,
    onHover = null,
    onNavigate = null,
    onClusterAction = null,
    onFilterChange = null,
    isAcceptable = (f) => !!f.acceptKey
  } = opts;

  const root = document.createElement('div');
  root.className = 'pb-finding-list';
  root.setAttribute('role', 'list');
  root.tabIndex = -1;

  let current = [];
  let filter = opts.filter || '';
  let showMaintenance = opts.showMaintenance === true;
  const collapseClusters = opts.collapseClusters !== false;
  let pendingStructuralReconcile = false;
  /** id → row element, so update() can patch instead of rebuild. */
  const rowsById = new Map();

  const holdsFocus = () =>
    typeof document !== 'undefined' &&
    document.activeElement &&
    root.contains(document.activeElement);

  function visible(findings) {
    return findings
      .map((finding, index) => ({ finding, index }))
      .filter(({ finding }) => {
        if (!showMaintenance && finding.kind === 'maintenance' && finding.severity !== 'blocking')
          return false;
        if (filter === 'accepted') return finding.disposition === 'accepted';
        return finding.disposition !== 'accepted' && (!filter || finding.severity === filter);
      })
      .sort((a, b) => {
        const rank = (finding) => {
          const index = SEVERITY_ORDER.indexOf(finding.severity);
          return index < 0 ? SEVERITY_ORDER.length : index;
        };
        return rank(a.finding) - rank(b.finding) || a.index - b.index;
      })
      .map(({ finding }) => finding);
  }

  function renderShape(finding) {
    return [
      !!(finding.fix && onAction),
      !!(onAccept && isAcceptable(finding) && finding.disposition !== 'accepted'),
      !!locationLabel(finding),
      finding.disposition === 'accepted'
    ].join(':');
  }

  function buildRow(finding) {
    const row = document.createElement('article');
    row.className = `pb-finding pb-finding--${finding.severity}`;
    row.setAttribute('role', 'listitem');
    row.dataset.findingId = finding.id;
    row.dataset.renderShape = renderShape(finding);
    row._pbFinding = finding;
    // Roving tabindex: exactly one row is tabbable at a time.
    row.tabIndex = -1;

    const body = document.createElement('div');
    body.className = 'pb-finding-body';
    const title = document.createElement('div');
    title.className = 'pb-finding-title';
    title.textContent = finding.title || finding.checkId || 'Finding';
    body.appendChild(title);

    const where = locationLabel(finding);
    if (where) {
      const chip = document.createElement('span');
      chip.className = 'pb-finding-where';
      chip.textContent = where;
      body.appendChild(chip);
    }
    if (finding.disposition === 'accepted') {
      const note = document.createElement('span');
      note.className = 'pb-finding-accepted';
      note.textContent = finding.acceptedBy ? `Accepted by ${finding.acceptedBy}` : 'Accepted';
      body.appendChild(note);
    }
    row.appendChild(body);

    // Actions sit on their own line under the text, never squeezed beside it:
    // a side column in a 450px panel wrapped "Keep as is" onto two lines and
    // shrank the title to a third of the row. Both share one size so the pair
    // reads as one control row — the accept used to be `bare`, i.e. an
    // unstyled browser button beside a kit one.
    const actions = document.createElement('div');
    actions.className = 'pb-finding-actions';
    if (finding.fix && onAction) {
      // The verb comes from the finding (P4). Never "Fix" — that is both a
      // useless affordance and forty identically-named buttons for a screen
      // reader.
      const label = finding.fix.label || 'Open';
      actions.appendChild(
        createButton({
          variant: 'outline',
          size: 'sm',
          label,
          className: 'pb-finding-action',
          ariaLabel: `${label}: ${finding.title || finding.checkId}`,
          onClick: () => onAction(row._pbFinding)
        })
      );
    }
    if (onAccept && isAcceptable(finding) && finding.disposition !== 'accepted') {
      actions.appendChild(
        createButton({
          variant: 'ghost',
          size: 'sm',
          label: acceptVerb(finding),
          className: 'pb-finding-accept',
          ariaLabel: `${acceptVerb(finding)}: ${finding.title || finding.checkId}`,
          onAsyncClick: () => onAccept(row._pbFinding),
          busyLabel: finding.kind === 'maintenance' ? 'Ignoring…' : 'Keeping…',
          errorScope: 'Accept finding'
        })
      );
    }
    if (actions.childElementCount) row.appendChild(actions);

    if (onHover) {
      row.addEventListener('mouseenter', () => onHover(row._pbFinding));
      row.addEventListener('mouseleave', () => onHover(null));
    }
    if (onNavigate) {
      row.addEventListener('click', (event) => {
        if (event.target.closest('button, a, input, select, textarea')) return;
        onNavigate(row._pbFinding);
      });
    }
    return row;
  }

  function patchRow(row, finding) {
    row._pbFinding = finding;
    row.className = `pb-finding pb-finding--${finding.severity}`;
    const title = row.querySelector('.pb-finding-title');
    const text = finding.title || finding.checkId || 'Finding';
    if (title && title.textContent !== text) title.textContent = text;
    const where = row.querySelector('.pb-finding-where');
    if (where) where.textContent = locationLabel(finding);
    const accepted = row.querySelector('.pb-finding-accepted');
    if (accepted) {
      accepted.textContent = finding.acceptedBy ? `Accepted by ${finding.acceptedBy}` : 'Accepted';
    }
    const action = row.querySelector('.pb-finding-action');
    if (action && finding.fix) {
      const label = finding.fix.label || 'Open';
      action.textContent = label;
      action.setAttribute('aria-label', `${label}: ${text}`);
    }
    const accept = row.querySelector('.pb-finding-accept');
    if (accept) {
      accept.textContent = acceptVerb(finding);
      accept.setAttribute('aria-label', `${acceptVerb(finding)}: ${text}`);
    }
    return row;
  }

  /**
   * A cluster reads as ONE row that opens: the same severity bar, title and
   * meta line as a single finding, with the count in the meta line rather than
   * four controls competing for one header row. When it can collapse, the
   * whole summary is the disclosure button — a chevron plus the name — so the
   * hit target is the thing you are reading, not a "Show 196 items" link at
   * the far edge.
   */
  function buildCluster(cluster, rowFor = buildRow) {
    if (!cluster.key) return rowFor(cluster.rows[0]);
    const section = document.createElement('section');
    section.className = `pb-finding-cluster pb-finding-cluster--${clusterSeverity(cluster.rows)}`;
    const header = document.createElement('div');
    header.className = 'pb-finding-cluster-header';

    // Real text separators, not CSS ones: when this is the toggle, its content
    // IS its accessible name, and "Same remedy6 items3 pages" is what three
    // adjacent spans read as.
    const label = document.createElement('span');
    label.className = 'pb-finding-cluster-label';
    label.textContent = cluster.label;
    const meta = document.createElement('span');
    meta.className = 'pb-finding-cluster-meta';
    const count = document.createElement('span');
    count.className = 'pb-finding-cluster-count';
    count.textContent = `${cluster.rows.length} items`;
    const pages = document.createElement('span');
    pages.className = 'pb-finding-cluster-pages';
    const pageCount = pagesAffected(cluster.rows);
    pages.textContent = `${pageCount} page${pageCount === 1 ? '' : 's'}`;
    const sep = document.createElement('span');
    sep.className = 'pb-finding-cluster-sep';
    sep.textContent = ' · ';
    sep.hidden = pages.hidden = pageCount === 0;
    meta.append(count, sep, pages);
    const pause = document.createElement('span');
    pause.className = 'pb-sr-only';
    pause.textContent = ', ';
    const text = document.createElement('span');
    text.className = 'pb-finding-cluster-text';
    text.append(label, pause, meta);

    const collapsible = collapseClusters && cluster.rows.length > 5;
    let expanded = !collapsible || savedClusterExpansion(cluster.key) === true;
    if (collapsible) {
      const toggle = createButton({
        variant: 'bare',
        icon: icon('chevronRight', { size: 'sm' }),
        iconClass: 'pb-finding-chevron',
        content: text,
        ariaExpanded: expanded,
        className: 'pb-finding-cluster-toggle',
        onClick: () => {
          expanded = !expanded;
          saveClusterExpansion(cluster.key, expanded);
          const focused = document.activeElement;
          for (const row of section.querySelectorAll('.pb-finding')) row.hidden = !expanded;
          toggle.setAttribute('aria-expanded', String(expanded));
          const previous = root.querySelector('.pb-finding:not([hidden])[tabindex="0"]');
          setRovingTarget(previous || root.querySelector('.pb-finding:not([hidden])'));
          if (focused?.closest('.pb-finding[hidden]')) toggle.focus();
        }
      });
      header.appendChild(toggle);
      // Rows indent past the chevron so they sit under the label.
      section.classList.add('pb-finding-cluster--collapsible');
    } else {
      text.classList.add('pb-finding-cluster-summary');
      header.appendChild(text);
    }
    const clusterFix = cluster.rows[0]?.clusterFix;
    if (clusterFix && onClusterAction) {
      header.appendChild(
        createButton({
          variant: 'outline',
          size: 'sm',
          label: clusterFix.label || 'Fix all',
          className: 'pb-finding-cluster-fix',
          ariaLabel: `${clusterFix.label || 'Fix all'}: ${cluster.label}`,
          onClick: () => onClusterAction(clusterFix.kind, cluster.rows)
        })
      );
    }
    section.appendChild(header);
    for (const finding of cluster.rows) {
      const row = rowFor(finding);
      row.hidden = !expanded;
      section.appendChild(row);
    }
    return section;
  }

  function refreshClusterSummary(section) {
    const rows = [...section.querySelectorAll('.pb-finding')];
    const findings = rows.map((row) => row._pbFinding);
    if (!findings.length) return;
    const label = section.querySelector('.pb-finding-cluster-label');
    if (label) {
      label.textContent = findings[0].groupLabel || findings[0].title || findings[0].groupKey;
    }
    const count = section.querySelector('.pb-finding-cluster-count');
    if (count) count.textContent = `${findings.length} items`;
    const pages = section.querySelector('.pb-finding-cluster-pages');
    if (pages) {
      const pageCount = pagesAffected(findings);
      pages.textContent = `${pageCount} page${pageCount === 1 ? '' : 's'}`;
      pages.hidden = pageCount === 0;
      const sep = section.querySelector('.pb-finding-cluster-sep');
      if (sep) sep.hidden = pages.hidden;
    }
    for (const severity of SEVERITY_ORDER) {
      section.classList.remove(`pb-finding-cluster--${severity}`);
    }
    section.classList.add(`pb-finding-cluster--${clusterSeverity(findings)}`);
  }

  function reconcile() {
    const previousRovingId = [...rowsById].find(([, row]) => row.tabIndex === 0)?.[0] || null;
    const rows = visible(current);
    const fragment = document.createDocumentFragment();
    const nextRowsById = new Map();
    if (rows.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'pb-finding-empty';
      if (filter) {
        const label = filter === 'accepted' ? 'Accepted' : severityLabel(filter);
        const message = document.createElement('p');
        message.textContent = `No "${label}" findings.`;
        empty.append(
          message,
          createButton({
            label: 'Show all',
            variant: 'ghost',
            size: 'sm',
            onClick: () => setFilter('')
          })
        );
      } else empty.textContent = 'Nothing to look at.';
      fragment.appendChild(empty);
    } else {
      const rowFor = (finding) => {
        let row = rowsById.get(finding.id) || null;
        if (!row || row.dataset.renderShape !== renderShape(finding)) {
          row = buildRow(finding);
        } else {
          patchRow(row, finding);
        }
        row.hidden = false;
        nextRowsById.set(finding.id, row);
        return row;
      };
      for (const cluster of clusterFindings(rows)) {
        fragment.appendChild(buildCluster(cluster, rowFor));
      }
    }
    root.replaceChildren(fragment);
    rowsById.clear();
    for (const [id, row] of nextRowsById) rowsById.set(id, row);
    const previous = rowsById.get(previousRovingId);
    setRovingTarget(
      previous && !previous.hidden ? previous : root.querySelector('.pb-finding:not([hidden])')
    );
    pendingStructuralReconcile = false;
  }

  function setRovingTarget(el) {
    for (const row of root.querySelectorAll('.pb-finding')) row.tabIndex = -1;
    if (el && !el.hidden) el.tabIndex = 0;
  }

  /**
   * Replace the finding set.
   *
   * When the list holds focus, ONLY text is patched — no node is added, moved
   * or removed. That is the whole point: the cheap-tier audit fires every
   * 300ms while the operator types, and a structural rebuild under focus is how
   * the previous renderer ate keystrokes.
   */
  function update(nextFindings) {
    const next = Array.isArray(nextFindings) ? nextFindings : [];
    if (holdsFocus()) {
      current = next;
      const desiredIds = new Set(visible(next).map((finding) => finding.id));
      const focusedRow = document.activeElement?.closest?.('.pb-finding');
      if (focusedRow && !desiredIds.has(focusedRow.dataset.findingId)) {
        removeFinding(focusedRow.dataset.findingId, desiredIds);
      }
      for (const finding of next) {
        const el = rowsById.get(finding.id);
        if (!el) continue;
        patchRow(el, finding);
      }
      for (const cluster of root.querySelectorAll('.pb-finding-cluster'))
        refreshClusterSummary(cluster);
      pendingStructuralReconcile = true;
      return { patched: true };
    }
    current = next;
    reconcile();
    return { patched: false };
  }

  /**
   * Remove one row without disturbing its neighbours — the "row leaves after
   * you fix it" path. Returns the element that received focus, if any.
   */
  function removeFinding(id, allowedIds = null) {
    const el = rowsById.get(id);
    if (!el) return null;
    const wasFocused = document.activeElement === el || el.contains(document.activeElement);
    const siblings = [...root.querySelectorAll('.pb-finding:not([hidden])')];
    const index = siblings.indexOf(el);
    const candidates = [...siblings.slice(index + 1), ...siblings.slice(0, index).reverse()];
    const heir =
      candidates.find((row) => !allowedIds || allowedIds.has(row.dataset.findingId)) || null;
    const parent = el.parentElement;
    el.remove();
    rowsById.delete(id);
    current = current.filter((f) => f.id !== id);
    // A cluster whose last row left takes its chrome with it.
    if (parent && parent.classList.contains('pb-finding-cluster')) {
      if (!parent.querySelector('.pb-finding')) parent.remove();
      else refreshClusterSummary(parent);
    }
    if (heir) {
      setRovingTarget(heir);
      // Focus recovery: when the row the operator was ON disappears, focus must
      // land somewhere deterministic rather than falling to <body>, which
      // silently ejects a keyboard user from the list.
      if (wasFocused) heir.focus();
      return heir;
    }
    if (wasFocused) root.focus?.();
    return null;
  }

  function setFilter(next) {
    const focused = holdsFocus() ? document.activeElement : null;
    filter = next || '';
    // User navigation takes effect now; only background report updates defer.
    reconcile();
    if (focused) (focused.isConnected && !focused.closest('[hidden]') ? focused : root).focus();
    onFilterChange?.(filter);
    return filter;
  }

  // A report may add/remove siblings while a text field or row action is
  // focused. Reconcile the queued structure as soon as focus genuinely leaves
  // the list; without this, the list can remain stale forever if no later audit
  // event happens.
  root.addEventListener('focusout', () => {
    queueMicrotask(() => {
      if (pendingStructuralReconcile && !holdsFocus()) reconcile();
    });
  });

  // Keyboard model. Roving tabindex over rows; the actions inside a row are
  // reachable with Tab as normal.
  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && filter) {
      event.preventDefault();
      setFilter('');
      return;
    }

    const rows = [...root.querySelectorAll('.pb-finding:not([hidden])')];
    if (rows.length === 0) return;
    const active = document.activeElement?.closest?.('.pb-finding');
    const rowFocused = active && document.activeElement === active;
    const index = active ? rows.indexOf(active) : -1;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const nextEl = rows[Math.max(0, Math.min(rows.length - 1, index + delta))] || rows[0];
      setRovingTarget(nextEl);
      nextEl.focus();
      return;
    }
    if (!active) return;
    const finding = current.find((f) => f.id === active.dataset.findingId);
    if (!finding) return;
    if (rowFocused && event.key === 'Enter' && !event.altKey && finding.fix && onAction) {
      event.preventDefault();
      onAction(finding);
      return;
    }
    if (rowFocused && event.key === 'Enter' && event.altKey && onAccept && isAcceptable(finding)) {
      event.preventDefault();
      onAccept(finding);
      return;
    }
  });

  update(opts.findings || []);

  return {
    el: root,
    update,
    removeFinding,
    setFilter,
    getFilter: () => filter,
    setShowMaintenance(value) {
      showMaintenance = Boolean(value);
      setFilter(filter);
    },
    /** Test/diagnostic seam. */
    _rows: () =>
      [...root.querySelectorAll('.pb-finding:not([hidden])')].map((el) => el.dataset.findingId)
  };
}

/** Severity pill copy, shared with the panel so the words cannot drift. */
export function severityLabel(severity) {
  return SEVERITY_LABEL[severity] || severity;
}

/** One-sentence readiness summary. Never a chip cluster. */
export function readinessSentence(counts, { hiddenMaintenance = 0 } = {}) {
  const suffix =
    hiddenMaintenance > 0
      ? ` ${hiddenMaintenance} maintenance check${hiddenMaintenance === 1 ? '' : 's'} hidden.`
      : '';
  const blocking = counts?.blocking || 0;
  const warning = counts?.warning || 0;
  const cleanup = counts?.cleanup || 0;
  if (blocking > 0) {
    return `${blocking} publish check${blocking === 1 ? '' : 's'} need${blocking === 1 ? 's' : ''} attention.${suffix}`;
  }
  const soft = warning + cleanup;
  if (soft === 0) return `Ready to publish.${suffix}`;
  return `Ready, with ${soft} thing${soft === 1 ? '' : 's'} worth a look.${suffix}`;
}

/** Escaped, for the surfaces that build HTML strings rather than nodes. */
export function escapeForList(value) {
  return escapeHTML(String(value ?? ''));
}
